/**
 * Kindly Reply landing page behaviour (template product.landing).
 * Loaded with `defer` from layout/landing.liquid. No dependencies.
 *
 * - Keeps every buy form, the hero media, the demo and the URL in sync with the
 *   selected style (variant).
 * - Turns "Get started" into: clear cart, add the chosen style, open checkout.
 *   Without JavaScript the form still posts to /cart/add and returns to checkout.
 * - Opens the live demo in a dialog only on allowed hosts, otherwise in a new tab.
 * - Plays the hero loop only when the hero is on screen and motion is allowed.
 * - Shows the sticky buy bar between the hero form and the final call to action.
 * - Publishes exactly three custom events through Shopify.analytics.publish for
 *   the kr-landing-events custom pixel: kr_demo_open { style, variant_id },
 *   kr_style_select { style, variant_id, location } and kr_faq_open { question }.
 *   Standard events (page view, view content, add to cart, checkout) are left to
 *   Shopify and the Meta app so nothing doubles.
 */
(function () {
  'use strict';

  if (window.KRLanding) return;

  /** @param {string} id */
  function readJSON(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch (err) {
      return null;
    }
  }

  const config = readJSON('kr-lp-config') || {};
  /** @type {Array<{id:number,available:boolean,name:string,handle:string,tagline:string,price:string,demoUrl:string,poster:string,posterSrcset:string,heroPoster:string,heroPosterSrcset:string,alt:string,video:string,hls:string}>} */
  const styles = readJSON('kr-lp-styles-data') || [];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** Hero media state: the page has loaded, the hero video is in view, and the latest poster swap. */
  let heroReady = false;
  let heroInView = false;
  let posterToken = 0;
  const allowedHosts = (config.allowedHosts || []).map((h) => String(h).trim().toLowerCase()).filter(Boolean);
  const messages = config.messages || {};

  const params = new URLSearchParams(window.location.search);
  // ?style=<style handle> (e.g. singapore-sling, rouge-velours) or ?variant=<id> preselects a style.
  // ?style wins when both are valid; an unknown value is ignored.
  const fromUrl = findStyleByHandle(params.get('style')) || findStyle(Number(params.get('variant')));
  let current = fromUrl || findStyle(Number(firstValue('[data-kr-lp-variant-input]'))) || styles[0] || null;

  /** @param {string | null} handle */
  function findStyleByHandle(handle) {
    const key = String(handle || '').trim().toLowerCase();
    if (!key) return null;
    for (const style of styles) if (style.handle && style.handle === key) return style;
    return null;
  }

  /** @param {string} selector */
  function firstValue(selector) {
    const el = document.querySelector(selector);
    return el && 'value' in el ? el.value : '';
  }

  /** @param {number} id */
  function findStyle(id) {
    if (!id) return null;
    for (const style of styles) if (style.id === id) return style;
    return null;
  }

  /**
   * @param {string} name
   * @param {Record<string, unknown>} [data]
   */
  function track(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data || {});
      }
    } catch (err) {
      /* analytics must never break the page */
    }
  }

  /* ---------- Style selection ---------- */

  /**
   * @param {number} id
   * @param {string} location hero, styles, sticky, final_cta or init
   */
  function selectStyle(id, location) {
    const style = findStyle(id);
    if (!style) return;
    const changed = !current || current.id !== style.id;
    current = style;

    for (const input of document.querySelectorAll('[data-kr-lp-variant-input]')) input.value = String(style.id);
    for (const radio of document.querySelectorAll('[data-kr-lp-style-radio]')) radio.checked = Number(radio.value) === style.id;
    for (const el of document.querySelectorAll('[data-kr-lp-price]')) el.textContent = style.price;
    for (const el of document.querySelectorAll('[data-kr-lp-style-name]')) el.textContent = style.name;
    for (const btn of document.querySelectorAll('[data-kr-lp-submit]')) btn.disabled = !style.available;
    for (const thumb of document.querySelectorAll('[data-kr-lp-thumb]')) {
      if (style.poster && thumb instanceof HTMLImageElement) {
        thumb.removeAttribute('srcset');
        thumb.src = style.poster;
      }
    }

    for (const card of document.querySelectorAll('[data-kr-lp-card]')) {
      const on = Number(card.getAttribute('data-kr-lp-card')) === style.id;
      card.classList.toggle('is-selected', on);
      const btn = card.querySelector('[data-kr-lp-select]');
      if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    updateHeroMedia(style);
    updateDemo(style);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(style.id));
    if (url.searchParams.has('style')) {
      if (style.handle) url.searchParams.set('style', style.handle);
      else url.searchParams.delete('style');
    }
    window.history.replaceState(window.history.state, '', url.toString());

    if (changed && location !== 'init') {
      track('kr_style_select', { style: style.name, variant_id: String(style.id), location: location });
    }
  }

  /**
   * Swaps the hero poster to the selected style once the new image has decoded, so the frame never
   * flashes empty. The image sits in a fixed aspect-ratio box, so the swap causes no layout shift.
   * @param {typeof styles[number]} style
   */
  function updateHeroPoster(style) {
    const img = document.querySelector('[data-kr-lp-poster]');
    // The phone shows the loop's first frame for styles with a loop, else the style poster.
    const src = style.heroPoster || style.poster;
    const srcset = style.heroPoster ? style.heroPosterSrcset : style.posterSrcset;
    if (!(img instanceof HTMLImageElement) || !src) return;
    if (img.getAttribute('data-kr-lp-poster-for') === String(style.id)) return;
    const token = ++posterToken;
    img.setAttribute('data-kr-lp-poster-for', String(style.id));
    const apply = () => {
      if (token !== posterToken) return;
      img.removeAttribute('srcset');
      img.src = src;
      if (srcset) img.srcset = srcset;
      img.alt = style.alt || style.name;
    };
    const pre = new Image();
    if (img.sizes) pre.sizes = img.sizes;
    if (srcset) pre.srcset = srcset;
    pre.src = src;
    if (typeof pre.decode === 'function') pre.decode().then(apply, apply);
    else apply();
  }

  /**
   * Fades the loop out, stops it and drops its source, so nothing of the previous style keeps
   * playing or downloading behind the poster.
   * @param {HTMLVideoElement} video
   */
  function unloadVideo(video) {
    video.classList.remove('is-playing');
    if (!video.paused) video.pause();
    if (video.dataset.loaded === 'true') {
      video.removeAttribute('src');
      video.load();
      video.dataset.loaded = 'false';
    }
  }

  /**
   * The rule: never show a video of a different style. A style without a loop shows its poster; a
   * style with a loop plays its own loop once the page has loaded and the hero is in view.
   * @param {typeof styles[number]} style
   */
  function updateHeroMedia(style) {
    updateHeroPoster(style);
    const video = heroVideo();
    if (!video) return;
    if (style.heroPoster || style.poster) video.poster = style.heroPoster || style.poster;
    const next = pickSource(style.video, style.hls);
    const current = pickSource(video.dataset.src, video.dataset.hls);
    if (next !== current) {
      video.dataset.src = style.video || '';
      video.dataset.hls = style.hls || '';
      unloadVideo(video);
    }
    if (next && heroReady && heroInView) playIfAllowed(video);
  }

  /** @param {typeof styles[number]} style */
  function updateDemo(style) {
    const url = style.demoUrl || '';
    for (const link of document.querySelectorAll('[data-kr-lp-demo-link], [data-kr-lp-demo-tab]')) {
      if (url) link.setAttribute('href', url);
    }
    for (const link of document.querySelectorAll('[data-kr-lp-demo-link]')) link.hidden = !url;
    for (const note of document.querySelectorAll('[data-kr-lp-demo-none]')) note.hidden = Boolean(url);
    for (const btn of document.querySelectorAll('button[data-kr-lp-demo-open]')) btn.disabled = !url;
    const poster = document.querySelector('[data-kr-lp-demo-poster]');
    if (poster && style.poster && !poster.closest('[data-kr-lp-demo-fixed]')) {
      poster.removeAttribute('srcset');
      poster.src = style.poster;
      if (style.posterSrcset) poster.srcset = style.posterSrcset;
    }
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-kr-lp-style-radio]')) {
      const form = target.closest('[data-kr-lp-buy]');
      selectStyle(Number(target.value), form ? form.getAttribute('data-location') || 'hero' : 'hero');
    }
  });

  /* ---------- Attribution ---------- */

  // UTMs and fbclid from the landing URL, kept for the browser session (so a reload or a
  // style change that rewrites the URL keeps them), then sent as cart attributes with the add.
  const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid'];
  const ATTRIBUTION_STORE = 'kr_lp_attribution';

  function readAttribution() {
    /** @type {Record<string, string>} */
    const fromUrl = {};
    for (const key of ATTRIBUTION_KEYS) {
      const value = params.get(key);
      if (value) fromUrl[key] = value.slice(0, 200);
    }
    try {
      if (Object.keys(fromUrl).length) {
        window.sessionStorage.setItem(ATTRIBUTION_STORE, JSON.stringify(fromUrl));
        return fromUrl;
      }
      const stored = JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_STORE) || 'null');
      return stored && typeof stored === 'object' ? stored : {};
    } catch (err) {
      return fromUrl;
    }
  }
  const attribution = readAttribution();

  /** @param {typeof styles[number] | null} style */
  function cartAttributes(style) {
    /** @type {Record<string, string>} */
    const attrs = {};
    for (const key of ATTRIBUTION_KEYS) if (attribution[key]) attrs[key] = String(attribution[key]);
    if (style) attrs.landing_style = style.handle || style.name;
    return attrs;
  }

  /* ---------- Buy: straight to checkout ---------- */

  // The forms post to /cart/add without JavaScript. With JavaScript the add goes through
  // cart/add.js, which Shopify already reports as product_added_to_cart. Shopify also reports a
  // submit of a form whose action is /cart/add, so the action is dropped here to avoid a second
  // AddToCart for the same click.
  for (const form of document.querySelectorAll('form[data-kr-lp-buy]')) {
    const action = form.getAttribute('action');
    if (action) {
      form.setAttribute('data-kr-lp-action', action);
      form.removeAttribute('action');
    }
  }

  let busy = false;

  /** @param {HTMLFormElement} form */
  async function buy(form) {
    if (busy) return;
    const input = form.querySelector('[data-kr-lp-variant-input]');
    const id = Number(input ? input.value : 0);
    if (!id) return;
    const style = findStyle(id);
    if (style && !style.available) return;

    busy = true;
    const button = form.querySelector('[data-kr-lp-submit]');
    const label = form.querySelector('[data-kr-lp-label]');
    const original = label ? label.textContent : '';
    const error = form.querySelector('[data-kr-lp-error]');
    if (button) button.setAttribute('aria-busy', 'true');
    if (label && messages.busy) label.textContent = messages.busy;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    const root = config.cartRoot || '/';
    const base = root.endsWith('/') ? root : root + '/';
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    try {
      await fetch(base + 'cart/clear.js', { method: 'POST', headers: headers });
      const response = await fetch(base + 'cart/add.js', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }], attributes: cartAttributes(style || null) })
      });
      if (!response.ok) throw new Error('cart add failed');
      window.location.href = config.checkoutUrl || '/checkout';
    } catch (err) {
      busy = false;
      if (button) button.removeAttribute('aria-busy');
      if (label) label.textContent = original;
      if (error) {
        error.textContent = messages.error || '';
        error.hidden = false;
      }
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.matches('[data-kr-lp-buy]')) {
      event.preventDefault();
      buy(form);
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    busy = false;
    for (const btn of document.querySelectorAll('[data-kr-lp-submit][aria-busy]')) btn.removeAttribute('aria-busy');
  });

  /* ---------- "What happens after you buy" (reused kr-after-you-buy section) ---------- */

  // Its "Order yours now" link scrolls to the hero buy box, so the selected style, kr events and the
  // UTM cart attributes all go through the landing buy form. Capture phase, so the section's own
  // product-page scroll handler never runs here.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target && target.closest('.kr-ab__cta[data-scroll-to-buy]');
      if (!link) return;
      const heroBuy = document.querySelector('#kr-lp-hero [data-kr-lp-buy]');
      if (!heroBuy) return;
      event.preventDefault();
      event.stopPropagation();
      heroBuy.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
      const submit = heroBuy.querySelector('[data-kr-lp-submit]');
      if (submit instanceof HTMLElement) window.setTimeout(() => submit.focus({ preventScroll: true }), reduceMotion.matches ? 0 : 450);
    },
    true
  );

  /* ---------- Style cards ---------- */

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const select = target.closest('[data-kr-lp-select]');
    if (select) {
      selectStyle(Number(select.getAttribute('data-kr-lp-select')), 'styles');
      const heroBuy = document.querySelector('#kr-lp-hero [data-kr-lp-buy]');
      if (heroBuy) {
        heroBuy.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
        const submit = heroBuy.querySelector('[data-kr-lp-submit]');
        if (submit instanceof HTMLElement) window.setTimeout(() => submit.focus({ preventScroll: true }), reduceMotion.matches ? 0 : 450);
      }
      return;
    }

    const opener = target.closest('[data-kr-lp-demo-open]');
    if (opener) {
      const style = findStyle(Number(opener.getAttribute('data-kr-lp-variant'))) || current;
      if (openDemo(opener, style)) event.preventDefault();
      else if (opener instanceof HTMLButtonElement) event.preventDefault();
    }
  });

  /* ---------- Live demo ---------- */

  const modal = document.querySelector('[data-kr-lp-demo-modal]');
  let lastFocus = null;
  let frameTimer = 0;

  function hostAllowed() {
    return allowedHosts.indexOf(window.location.hostname.toLowerCase()) !== -1;
  }

  /**
   * Opens the demo. Returns true when the page handled it (dialog or window.open),
   * false when the browser should follow the link's own target="_blank".
   * @param {Element} opener
   * @param {typeof styles[number] | null} style The style to preview (defaults to the selected one).
   */
  function openDemo(opener, style) {
    const url = style && style.demoUrl;
    if (!url) {
      const section = document.getElementById('kr-lp-demo');
      if (section) section.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      return true;
    }

    track('kr_demo_open', { style: style ? style.name : '', variant_id: style ? String(style.id) : '' });
    const canFrame = modal instanceof HTMLDialogElement && typeof modal.showModal === 'function' && hostAllowed();
    if (!canFrame) {
      if (opener instanceof HTMLAnchorElement && opener.target === '_blank' && opener.getAttribute('href') === url) return false;
      window.open(url, '_blank', 'noopener');
      return true;
    }

    lastFocus = document.activeElement;
    const body = modal.querySelector('[data-kr-lp-demo-body]');
    const tab = modal.querySelector('[data-kr-lp-demo-tab]');
    if (tab) tab.setAttribute('href', url);
    const title = modal.querySelector('[data-kr-lp-style-name]');
    if (title && style) title.textContent = style.name;
    if (body) {
      body.textContent = '';
      const frame = document.createElement('iframe');
      frame.src = url;
      frame.title = (style && style.name ? style.name + ' ' : '') + 'live invite';
      frame.setAttribute('allow', 'autoplay; fullscreen');
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      let loaded = false;
      frame.addEventListener('load', () => {
        loaded = true;
      });
      body.appendChild(frame);
      window.clearTimeout(frameTimer);
      frameTimer = window.setTimeout(() => {
        if (!loaded) showFrameFallback(body, url);
      }, 10000);
    }
    modal.showModal();
    const close = modal.querySelector('[data-kr-lp-demo-close]');
    if (close instanceof HTMLElement) close.focus();
    return true;
  }

  /**
   * @param {Element} body
   * @param {string} url
   */
  function showFrameFallback(body, url) {
    body.textContent = '';
    const p = document.createElement('p');
    p.className = 'kr-lp-modal__msg';
    const a = document.createElement('a');
    a.className = 'kr-lp-link';
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = 'Open it in a new tab';
    p.append('The live invite is taking a while to load here. ', a);
    body.appendChild(p);
  }

  function closeDemo() {
    if (!(modal instanceof HTMLDialogElement)) return;
    if (modal.open) modal.close();
  }

  if (modal instanceof HTMLDialogElement) {
    modal.addEventListener('close', () => {
      window.clearTimeout(frameTimer);
      const body = modal.querySelector('[data-kr-lp-demo-body]');
      if (body) body.textContent = '';
      if (lastFocus instanceof HTMLElement) lastFocus.focus();
    });
    modal.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target === modal) closeDemo();
      if (target && target.closest('[data-kr-lp-demo-close]')) closeDemo();
    });
  }

  /* ---------- Hero loop ---------- */

  function heroVideo() {
    const v = document.querySelector('[data-kr-lp-hero-video]');
    return v instanceof HTMLVideoElement ? v : null;
  }

  /**
   * The light MP4 only (480p or 720p, chosen in Liquid). HLS is no longer used: Chrome now plays HLS
   * natively and its adaptive stream climbs to the 1080p rendition (about 12 MB per loop) on fast
   * connections. With no light MP4 the poster stays.
   * @param {string | undefined} mp4
   * @param {string | undefined} hls
   */
  // eslint-disable-next-line no-unused-vars
  function pickSource(mp4, hls) {
    return mp4 || '';
  }

  /** @param {HTMLVideoElement} video */
  function playIfAllowed(video) {
    if (reduceMotion.matches) return;
    const connection = /** @type {any} */ (navigator).connection;
    if (connection && connection.saveData) return;
    if (video.dataset.loaded !== 'true') {
      const source = pickSource(video.dataset.src, video.dataset.hls);
      if (!source) return;
      video.preload = 'auto';
      video.src = source;
      video.dataset.loaded = 'true';
    }
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  const video = heroVideo();
  /** Starts the loop only after the page has loaded, so the video never competes with the first paint. */
  function afterLoad(fn) {
    if (document.readyState === 'complete') fn();
    else window.addEventListener('load', () => fn(), { once: true });
  }
  if (video) afterLoad(() => {
    heroReady = true;
    video.addEventListener('playing', () => {
      if (video.dataset.loaded === 'true' && pickSource(video.dataset.src, video.dataset.hls)) video.classList.add('is-playing');
    });
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            heroInView = entry.isIntersecting;
            if (entry.isIntersecting) playIfAllowed(video);
            else if (!video.paused) video.pause();
          }
        },
        { threshold: 0.25 }
      );
      io.observe(video);
    } else {
      heroInView = true;
      playIfAllowed(video);
    }
    reduceMotion.addEventListener('change', () => {
      if (reduceMotion.matches) video.pause();
    });
  });

  /* ---------- Sticky bar ---------- */

  const sticky = document.querySelector('[data-kr-lp-sticky]');
  const heroForm = document.querySelector('#kr-lp-hero [data-kr-lp-buy]');
  const finalSection = document.querySelector('[data-kr-lp-final]');
  let heroGone = false;
  let finalShowing = false;
  // Shown once the hero buy form has scrolled away; hidden while the hero or final buy form is in view.

  function renderSticky() {
    if (!sticky) return;
    const show = heroGone && !finalShowing;
    sticky.classList.toggle('is-visible', show);
    if (show) {
      sticky.removeAttribute('inert');
      sticky.removeAttribute('aria-hidden');
    } else {
      sticky.setAttribute('inert', '');
      sticky.setAttribute('aria-hidden', 'true');
    }
  }

  if (sticky && 'IntersectionObserver' in window) {
    const visibleOnThisScreen = window.getComputedStyle(sticky).display !== 'none';
    if (visibleOnThisScreen) document.body.style.setProperty('--kr-sticky-h', sticky.offsetHeight + 'px');
    if (heroForm) {
      new IntersectionObserver((entries) => {
        for (const entry of entries) heroGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        renderSticky();
      }).observe(heroForm);
    } else {
      heroGone = true;
    }
    if (finalSection) {
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) finalShowing = entry.isIntersecting;
          renderSticky();
        },
        { threshold: 0 }
      ).observe(finalSection);
    }
    renderSticky();
  }

  if (window.customElements && typeof window.customElements.whenDefined === 'function') {
    window.customElements.whenDefined('shopify-chat').then(() => document.documentElement.classList.add('kr-lp-has-chat'));
  }

  /* ---------- FAQ ---------- */

  document.addEventListener(
    'toggle',
    (event) => {
      const target = event.target;
      if (target instanceof HTMLDetailsElement && target.matches('[data-kr-lp-faq]') && target.open) {
        const q = target.querySelector('summary');
        track('kr_faq_open', { question: q ? q.textContent.trim() : '' });
      }
    },
    true
  );

  /* ---------- Init ---------- */

  // A style from ?style or ?variant updates the hero, picker, sticky bar and buy forms without
  // firing kr_style_select.
  if (current && fromUrl && fromUrl.id === current.id) {
    selectStyle(current.id, 'init');
  }

  window.KRLanding = { selectStyle: selectStyle, track: track };
})();
